import Check from '@mui/icons-material/Check';
import PersonAdd from '@mui/icons-material/PersonAdd';
import PlayArrow from '@mui/icons-material/PlayArrow';
import RemoveRedEye from '@mui/icons-material/RemoveRedEye';
import { Button, Card, CardActions, Divider } from '@mui/material';
import { Fare } from '@silbo/core';
import { palette } from '@silbo/ui';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';
import { useAssignation } from '../hooks/useAssignation';
import { useOtherAssignedPorters } from '../hooks/useOtherAssignedPorters';
import { FareStatusType, STATUSES } from '../types/fare-status';
import { FareButton } from './FareButton';
import FareCardContent from './FareCardContent';

type FareCardProps = {
  fare: Fare;
  onFareTick: (fare: Fare, title: string, content: () => JSX.Element) => void;
  onAcknowledge: (fare: Fare) => void;
  isOfflineMode: boolean;
};

const FareCard = ({ fare, onFareTick, isOfflineMode, onAcknowledge }: FareCardProps) => {
  const { t } = useTranslation('patient-transport');
  const { assignation } = useAssignation(fare);
  const { getPendingFareStatus, isAcknowledgeQueryPending } = useQueryQueueContext();

  const [pendingFare, setPendingFare] = useState<Fare | null>(null);
  const [isDoneOffline, setIsDoneOffline] = useState(false);

  const isAcknowledged = !!assignation?.acknowledgedAt || isAcknowledgeQueryPending(fare.id ?? '');

  const { otherAssignedPorters } = useOtherAssignedPorters(fare);

  useEffect(() => {
    const pendingStatus = getPendingFareStatus(fare.id);

    if (pendingStatus === STATUSES.ASSIGNED) {
      // @ts-expect-error pendingStatus is not null
      const fareCopy: Fare = { ...fare, status: STATUSES.STARTED as FareStatusType };
      setPendingFare(fareCopy);
    } else if (pendingStatus === STATUSES.STARTED) {
      // @ts-expect-error pendingStatus is not null
      const fareCopy: Fare = { ...fare, status: STATUSES.PATIENT_CARE as FareStatusType };
      setPendingFare(fareCopy);
    } else if (pendingStatus === STATUSES.PATIENT_CARE) {
      setIsDoneOffline(true);
    } else {
      setPendingFare(null);
    }
  }, [fare, getPendingFareStatus]);

  const handleFareTick = (title: string, content: () => JSX.Element) => {
    const fareToUse = pendingFare ?? fare;
    onFareTick(fareToUse, title, content);
  };

  let isAssigned = fare.status === STATUSES.ASSIGNED;
  let isStarted = fare.status === STATUSES.STARTED;
  let isPatientCare = fare.status === STATUSES.PATIENT_CARE;

  if (pendingFare) {
    if (pendingFare.status === STATUSES.STARTED) {
      isAssigned = false;
      isStarted = true;
    } else if (pendingFare.status === STATUSES.PATIENT_CARE) {
      isAssigned = false;
      isStarted = false;
      isPatientCare = true;
    }
  }

  return (
    <>
      <Card sx={{ border: isStarted || isPatientCare ? `2px solid ${palette.primary.main}` : 'none' }}>
        <FareCardContent fare={fare} otherAssignedPorters={otherAssignedPorters} />
        <Divider />
        <CardActions sx={{ justifyContent: 'center', gap: 8 }} disableSpacing>
          <FareButton
            Icon={<RemoveRedEye />}
            disabled={isAcknowledged || isDoneOffline}
            success={isAcknowledged || isDoneOffline}
            isOfflineMode={isOfflineMode}
            onClick={() => onAcknowledge(fare)}
          />
          {isAcknowledged && (
            <>
              <FareButton
                Icon={<PlayArrow />}
                disabled={isStarted || isPatientCare || isDoneOffline || !isAcknowledged}
                success={isStarted || isPatientCare || isDoneOffline}
                isOfflineMode={isOfflineMode}
                onClick={() => handleFareTick(t('fare.actions.start'), () => <PlayArrow />)}
              />
              <FareButton
                Icon={<PersonAdd />}
                disabled={isAssigned || isPatientCare || isDoneOffline}
                success={isPatientCare || isDoneOffline}
                isOfflineMode={isOfflineMode}
                onClick={() => handleFareTick(t('fare.actions.patientCare'), () => <PersonAdd />)}
              />
              <FareButton
                Icon={<Check />}
                disabled={isAssigned || isStarted || isDoneOffline}
                success={isOfflineMode ? isDoneOffline || false : false}
                isOfflineMode={isOfflineMode}
                onClick={() => handleFareTick(t('fare.actions.finish'), () => <Check />)}
              />
            </>
          )}
        </CardActions>
        <Divider />
        <CardActions sx={{ justifyContent: 'center' }}>
          <Link to="/patient-transport/fares/active/$id/report-incident" params={{ id: fare.id }}>
            <Button variant="text" color="error">
              {t('reportIncident.title')}
            </Button>
          </Link>
        </CardActions>
      </Card>
    </>
  );
};

export default FareCard;
